/**
 * Comprehensive Sentry debugging and testing component
 */

import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  captureException,
  captureMessage,
  addBreadcrumb,
  setUserContext,
  clearUserContext,
} from "../../config/sentry";
import * as Sentry from "@sentry/react";
import { Bug, CheckCircle, XCircle, AlertTriangle, Info } from "lucide-react";

export const SentryDebugTest: React.FC = () => {
  const [testResults, setTestResults] = useState<string[]>([]);
  const [isSentryConfigured, setIsSentryConfigured] = useState<boolean | null>(
    null,
  );

  const addResult = (
    message: string,
    type: "success" | "error" | "warning" | "info" = "info",
  ) => {
    const timestamp = new Date().toLocaleTimeString();
    const result = `[${timestamp}] ${message}`;
    setTestResults((prev) => [...prev, result]);
    console.log(`SentryDebugTest [${type.toUpperCase()}]:`, message);
  };

  const checkSentryConfiguration = () => {
    addResult("🔍 Checking Sentry configuration...", "info");

    // Check if Sentry is initialized by trying to capture a message
    try {
      const testEventId = Sentry.captureMessage(
        "Sentry configuration test",
        "debug",
      );
      if (testEventId) {
        addResult("✅ Sentry client is initialized", "success");
        setIsSentryConfigured(true);
        addResult(`Test event ID: ${testEventId}`, "info");
      } else {
        addResult("❌ Sentry client is not initialized", "error");
        setIsSentryConfigured(false);
      }
    } catch (error) {
      addResult(`❌ Sentry client error: ${error}`, "error");
      setIsSentryConfigured(false);
    }
  };

  const testErrorCapture = () => {
    addResult("🧪 Testing error capture...", "info");
    try {
      // Create a test error
      const error = new Error("Test error for Sentry debugging");
      error.name = "SentryDebugTestError";

      // Capture the error
      const eventId = captureException(error, {
        component: "SentryDebugTest",
        action: "testErrorCapture",
        timestamp: new Date().toISOString(),
      });

      addResult(`✅ Error captured with ID: ${eventId}`, "success");
    } catch (err) {
      addResult(`❌ Error capture failed: ${err}`, "error");
    }
  };

  const testMessageCapture = () => {
    addResult("📝 Testing message capture...", "info");
    try {
      const eventId = captureMessage(
        "Test message from SentryDebugTest",
        "info",
        {
          component: "SentryDebugTest",
          action: "testMessageCapture",
          timestamp: new Date().toISOString(),
        },
      );

      addResult(`✅ Message captured with ID: ${eventId}`, "success");
    } catch (err) {
      addResult(`❌ Message capture failed: ${err}`, "error");
    }
  };

  const testBreadcrumb = () => {
    addResult("🍞 Testing breadcrumb...", "info");
    try {
      addBreadcrumb(
        "Test breadcrumb from SentryDebugTest",
        "user-action",
        "info",
        {
          component: "SentryDebugTest",
          timestamp: new Date().toISOString(),
        },
      );

      addResult("✅ Breadcrumb added successfully", "success");
    } catch (err) {
      addResult(`❌ Breadcrumb failed: ${err}`, "error");
    }
  };

  const testUserContext = () => {
    addResult("👤 Testing user context...", "info");
    try {
      setUserContext({
        id: "debug-test-user",
        email: "debug@example.com",
        username: "debuguser",
      });

      addResult("✅ User context set successfully", "success");

      // Clear after 2 seconds
      setTimeout(() => {
        clearUserContext();
        addResult("✅ User context cleared", "info");
      }, 2000);
    } catch (err) {
      addResult(`❌ User context failed: ${err}`, "error");
    }
  };

  const testNetworkRequest = async () => {
    addResult("🌐 Testing network request...", "info");
    try {
      // Use Sentry.withScope for performance monitoring
      Sentry.withScope((scope) => {
        scope.setTag("test", "network-request");
        scope.setContext("network", {
          url: "https://api.example.com/test",
          method: "GET",
        });

        // Simulate a network request
        addResult("📡 Simulating network request...", "info");

        // Simulate some work
        setTimeout(() => {
          addResult("✅ Network request simulation completed", "success");
        }, 100);
      });
    } catch (err) {
      addResult(`❌ Network test failed: ${err}`, "error");
    }
  };

  const clearResults = () => {
    setTestResults([]);
  };

  const getStatusIcon = (type: string) => {
    switch (type) {
      case "success":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "error":
        return <XCircle className="h-4 w-4 text-red-500" />;
      case "warning":
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      default:
        return <Info className="h-4 w-4 text-blue-500" />;
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bug className="h-5 w-5" />
            Sentry Debug Test Suite
          </CardTitle>
          <CardDescription>
            Comprehensive testing and debugging for Sentry integration
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status Alert */}
          {isSentryConfigured !== null && (
            <Alert>
              {isSentryConfigured ? (
                <CheckCircle className="h-4 w-4" />
              ) : (
                <XCircle className="h-4 w-4" />
              )}
              <AlertDescription>
                {isSentryConfigured
                  ? "Sentry is properly configured and initialized"
                  : "Sentry is not configured or failed to initialize"}
              </AlertDescription>
            </Alert>
          )}

          {/* Test Buttons */}
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={checkSentryConfiguration} variant="outline">
              Check Configuration
            </Button>
            <Button onClick={testErrorCapture} variant="outline">
              Test Error Capture
            </Button>
            <Button onClick={testMessageCapture} variant="outline">
              Test Message Capture
            </Button>
            <Button onClick={testBreadcrumb} variant="outline">
              Test Breadcrumb
            </Button>
            <Button onClick={testUserContext} variant="outline">
              Test User Context
            </Button>
            <Button onClick={testNetworkRequest} variant="outline">
              Test Network Request
            </Button>
          </div>

          <div className="flex gap-2">
            <Button onClick={clearResults} variant="secondary" size="sm">
              Clear Results
            </Button>
            <Button
              onClick={() => {
                addResult("🔄 Running all tests...", "info");
                checkSentryConfiguration();
                setTimeout(() => testErrorCapture(), 100);
                setTimeout(() => testMessageCapture(), 200);
                setTimeout(() => testBreadcrumb(), 300);
                setTimeout(() => testUserContext(), 400);
                setTimeout(() => testNetworkRequest(), 500);
              }}
              variant="default"
              size="sm"
            >
              Run All Tests
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {testResults.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Test Results</CardTitle>
            <CardDescription>
              Real-time test results and debugging information
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {testResults.map((result, index) => (
                <div key={index} className="flex items-center gap-2 text-sm">
                  {getStatusIcon(
                    result.includes("✅")
                      ? "success"
                      : result.includes("❌")
                      ? "error"
                      : "info",
                  )}
                  <span className="font-mono">{result}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>Debugging Instructions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p>
            1. <strong>Check Configuration</strong> - Verify Sentry is properly
            initialized
          </p>
          <p>
            2. <strong>Run Tests</strong> - Execute various Sentry functions
          </p>
          <p>
            3. <strong>Check Browser Console</strong> - Look for detailed Sentry
            logs
          </p>
          <p>
            4. <strong>Check Network Tab</strong> - Look for requests to Sentry
            API
          </p>
          <p>
            5. <strong>Check Sentry Dashboard</strong> - Verify events are being
            received
          </p>
          <p className="text-muted-foreground">
            If Sentry is not configured, set the VITE_SENTRY_DSN environment
            variable.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
